class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.scrollBarPadding = getScrollBarWidth();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // maintain focus
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seems to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    // focus trap
    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    // add scrollbar padding to body
    addScrollbarPadding();
    document.body.classList.add('overflow-hidden');
    // ------------TD CHANGE-----------
    document.body.classList.add('td-cart-drawer-open');
    // ----------END TD CHANGE---------
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    removeScrollbarPadding();
    document.body.classList.remove('overflow-hidden');
    // ------------TD CHANGE-----------
    document.body.classList.remove('td-cart-drawer-open');
    // ----------END TD CHANGE---------
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // ------------TD CHANGE-----------
    const cartDrawerHtml = parsedState?.sections?.['cart-drawer'];
    let isEmpty = typeof parsedState?.item_count === 'number' ? parsedState.item_count === 0 : null;

    if (typeof cartDrawerHtml === 'string') {
      const parsedDrawer = this.getSectionDOM(cartDrawerHtml);
      const nextDrawerRoot = parsedDrawer?.querySelector('cart-drawer');
      if (nextDrawerRoot) {
        isEmpty = nextDrawerRoot.classList.contains('is-empty');
      }
    }

    if (typeof isEmpty === 'boolean') {
      this.classList.toggle('is-empty', isEmpty);
    }
    // ----------END TD CHANGE---------

    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartUpsell extends HTMLElement {
  constructor() {
    super();

    this.productId = this.getAttribute('data-product-id');
    this.addBtn = this.querySelector('[data-upsell-add]');
    this.cartDrawer = document.querySelector('cart-drawer');
    this.addBtn.addEventListener('click', this.addUpsellItem.bind(this));
  }

  addUpsellItem() {
    if (!this.productId) return;

    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    // ------------TD CHANGE-----------
    if (this.addBtn instanceof HTMLButtonElement) {
      this.addBtn.setAttribute('aria-busy', 'true');
      this.addBtn.disabled = true;
    }
    // ----------END TD CHANGE---------

    const data = {
      items: [{
        quantity: 1,
        id: this.productId
      }],
      sections: this.cartDrawer.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname
    }

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
    .then((response) => {
      return response.text();
    })
    .then((state) => {
      const parsedState = JSON.parse(state);
      this.cartDrawer.renderContents(parsedState);
    })
    .catch((error) => {
      console.error('Error:', error);
    })
    .finally(() => {
      // ------------TD CHANGE-----------
      if (this.addBtn instanceof HTMLButtonElement) {
        this.addBtn.removeAttribute('aria-busy');
        this.addBtn.disabled = false;
      }
      // ----------END TD CHANGE---------
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
    });
  }
}

customElements.define('cart-upsell', CartUpsell);
